import React, { useEffect, useState, useCallback } from "react";
import AdminSidebar from "./AdminSidebar";
import adminAxios from "./adminAxios";
import { useAuth } from "../context/AuthContext";
import { useOutletContext } from "react-router-dom";
import { 
  Users, 
  ShieldAlert, 
  Search, 
  Edit3, 
  Lock, 
  Key, 
  Wallet, 
  BarChart,
  UserCheck,
  XCircle,
  MoreVertical,
  Menu,
  CheckCircle2
} from "lucide-react";

// Premium Cyber Palette
const colors = {
  primary: "#03f8d7", // Cyan
  accent: "#ff2d75",  // Pink/Red
  surface: "rgba(13, 13, 18, 0.95)",
  deepBg: "#060608",
  border: "rgba(3, 248, 215, 0.15)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  success: "#10B981",
  warning: "#F59E0B"
};

function ManageUsers() {
  const { isOpen, setIsOpen } = useOutletContext();
  
  const { socket } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [walletModal, setWalletModal] = useState(null);
  const [walletAmount, setWalletAmount] = useState("");
  const [walletType, setWalletType] = useState("add");
  const [levelModal, setLevelModal] = useState(null);
  const [newLevel, setNewLevel] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [credModal, setCredModal] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [newPin, setNewPin] = useState("");

  // Retrieve isolated tenant context identifier
  const tenantId = localStorage.getItem("adminId") || "global";

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAxios.get("/api/admin/users", { params: { tenantId } });
      const data = res.data;
      setUsers(Array.isArray(data) ? data : (data.users || []));
    } catch (err) {
      console.error("[System] Registry Fetch Error:", err);
      setUsers([]);
      showFeedback("Registry synchronization failed.", "error");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchUsers();
    
    if (socket) {
      socket.emit("join", { userId: "admin", role: "admin", tenantId });

      socket.on("walletUpdate", (data) => {
        setUsers((prev) => 
          prev.map((u) => 
            (u._id === data.userId || u._id === data.id) 
              ? { ...u, creditBalance: data.newBalance } 
              : u
          )
        );
      });

      socket.on("adminRegistryUpdate", (data) => {
        setUsers((prev) => 
          prev.map((u) => 
            u._id === data.userId ? { ...u, creditBalance: data.newBalance } : u
          )
        );
      });

      socket.on("accountStatusUpdate", (data) => {
        setUsers((prev) => 
          prev.map((u) => 
            (u._id === data.userId || u._id === data.id) 
              ? { ...u, restricted: data.restricted, verified: data.verified ?? u.verified, incomeTaxPaid: data.incomeTaxPaid ?? u.incomeTaxPaid } 
              : u
          )
        );
      });
    }

    return () => {
      if (socket) {
        socket.off("walletUpdate");
        socket.off("adminRegistryUpdate");
        socket.off("accountStatusUpdate");
      }
    };
  }, [socket, fetchUsers, tenantId]);

  const showFeedback = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const approveUser = async (id) => {
    try {
      const targetId = String(id).trim();
      await adminAxios.put(`/api/admin/approve/${targetId}`, { tenantId });
      showFeedback("Node clearance granted.", "success");
      setUsers(prev => prev.map(u => String(u._id) === targetId ? { ...u, verified: true } : u));
    } catch (err) {
      showFeedback("Clearance authorization failed.", "error");
    }
  };

  const toggleIncomeTaxPayment = async (user) => {
    try {
      const targetState = !user.incomeTaxPaid;
      const targetId = String(user._id).trim();
      await adminAxios.put(`/api/admin/users/${targetId}/tax`, { 
        incomeTaxPaid: targetState,
        tenantId 
      });
      showFeedback(`Node tax marked as ${targetState ? "PAID" : "UNPAID"}.`, "success");
      setUsers(prev => prev.map(u => 
        String(u._id) === targetId ? { ...u, incomeTaxPaid: targetState } : u
      ));
    } catch (err) {
      showFeedback("Tax status modification failed.", "error");
    }
  };

  const toggleRestriction = async (user) => {
    try {
      const targetId = String(user._id).trim();
      await adminAxios.put(`/api/admin/restrict/${targetId}`, { 
        restricted: !user.restricted,
        tenantId 
      });
      showFeedback("Operational status updated.", "success");
      setUsers(prev => prev.map(u => 
        String(u._id) === targetId ? { ...u, restricted: !user.restricted } : u
      ));
    } catch (err) {
      showFeedback("Status update failed.", "error");
    }
  };

  const submitWalletAdjust = async () => {
    if (!walletModal || !walletAmount) return;
    try {
      const targetId = String(walletModal._id).trim();
      const res = await adminAxios.put(`/api/admin/users/${targetId}/wallet`, {
        amount: Number(walletAmount),
        type: walletType,
        tenantId
      });
      const updatedBalance = res.data.creditBalance;
      setWalletModal(null);
      setWalletAmount("");
      showFeedback("Asset allocation adjusted.", "success");
      setUsers(prev => prev.map(u => String(u._id) === targetId ? { ...u, creditBalance: updatedBalance } : u));
    } catch (err) {
      showFeedback("Adjustment failed.", "error");
    }
  };

  const submitLevelUpdate = async () => {
    if (!levelModal || !newLevel) return;
    try {
      const targetId = String(levelModal._id).trim();
      await adminAxios.put(`/api/admin/users/${targetId}/update-level`, { 
        level: Number(newLevel),
        tenantId 
      });
      setUsers(prev => prev.map(u => 
        String(u._id) === targetId ? { ...u, memberLevel: Number(newLevel), level: Number(newLevel), clearanceLevel: Number(newLevel) } : u
      ));
      setLevelModal(null);
      showFeedback("Clearance priority updated.", "success");
    } catch (err) {
      showFeedback("Priority update failed.", "error");
    }
  };

  const submitCredentialUpdate = async () => {
    if (!credModal) return;
    try {
      const targetId = String(credModal._id).trim();
      await adminAxios.put(`/api/admin/users/credentials`, {
        userId: targetId,
        newPassword: newPassword || undefined,
        newPin: newPin || undefined,
        tenantId
      });
      setCredModal(null);
      setNewPassword("");
      setNewPin("");
      showFeedback("Credentials updated.", "success");
    } catch (err) {
      showFeedback("Credential update failed.", "error");
    }
  };

  const executeDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const targetId = String(deleteConfirm._id).trim();
      await adminAxios.delete(`/api/admin/users/${targetId}`, { data: { tenantId } });
      setUsers((prev) => prev.filter((u) => String(u._id) !== targetId));
      setDeleteConfirm(null);
      showFeedback("Node record purged.", "success");
    } catch (err) {
      showFeedback("Purge command failed.", "error");
    }
  };

  const filtered = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.node_id?.toLowerCase().includes(search.toLowerCase())
  );

  const dynamicMainContent = {
    ...styles.mainContent,
    paddingLeft: isOpen ? "260px" : "0",
  };

  return (
    <div style={styles.container} data-tenant-scope={tenantId}>
      <AdminSidebar isOpen={isOpen} />
      
      <div style={dynamicMainContent}>
        
        {/* HEADER SECTION */}
        <div style={styles.topBar}>
          <div style={styles.headerText}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {!isOpen && (
                <button 
                   onClick={() => setIsOpen(true)}
                   style={styles.toggleBtn}
                   title="Open Sidebar"
                >
                   <Menu size={20} color={colors.primary} />
                </button>
              )}
              <Users color={colors.primary} size={22} />
              <h1 style={styles.title}>STATION REGISTRY</h1>
            </div>
            <p style={styles.subTitle}>NODE MONITORING & CLEARANCE PROTOCOLS</p>
          </div>
          <div style={styles.topBarStats}>
            <div style={styles.statLine}>
              <span style={styles.statLabel}>ACTIVE_NODES:</span>
              <span style={styles.statValue}>{users.length}</span>
            </div>
          </div>
        </div>

        {/* FEEDBACK TOAST */}
        {message.text && (
          <div style={{
            ...styles.toast, 
            borderColor: message.type === "success" ? colors.success : colors.accent,
            color: message.type === "success" ? colors.success : colors.accent
          }}>
            <ShieldAlert size={16} />
            {message.text.toUpperCase()}
          </div>
        )}

        <div style={styles.pagePadding}>
          {/* SEARCH BAR */}
          <div style={styles.searchWrapper}>
            <Search style={styles.searchIcon} size={18} />
            <input 
              style={styles.searchField}
              placeholder="SCAN FOR NODE ID, EMAIL, OR REGISTRY STRING..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* USER TABLE/LIST */}
          <div style={styles.listContainer}>
            {loading ? (
              <div style={styles.statusMsg}>SYNCHRONIZING SECURE DATA UPLINK...</div>
            ) : filtered.length === 0 ? (
              <div style={styles.statusMsg}>NULL RESULTS: NO MATCHING NODES FOUND</div>
            ) : (
              filtered.map((u) => (
                <div key={String(u._id)} style={styles.userBar}>
                  
                  <div style={styles.userMainInfo}>
                    <div style={styles.avatar}>
                      <UserCheck size={18} color={u.verified ? colors.primary : colors.textMuted} />
                    </div>
                    <div style={styles.nameSection}>
                      <div style={styles.userName}>{u.name || "UNIDENTIFIED NODE"}</div>
                      <div style={styles.userEmail}>{u.email}</div>
                    </div>
                  </div>

                  <div style={styles.idWrapper}>
                    <span style={styles.idLabel}>REGISTRY_CODE</span>
                    <code style={styles.highlightedId}>{u.node_id || String(u._id).slice(-6).toUpperCase()}</code>
                  </div>

                  <div style={styles.financeSection}>
                    <div style={styles.dataBlock}>
                      <span style={styles.dataLabel}>CREDIT_BALANCE</span>
                      <div style={styles.dataValue}>
                        <span style={{ color: colors.primary }}>$</span>
                        {Number(u.creditBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        <button onClick={() => { setWalletModal(u); setWalletAmount(""); }} style={styles.inlineEditBtn}>
                          <Edit3 size={10} />
                        </button>
                      </div>
                    </div>
                    <div style={styles.dataBlock}>
                      <span style={styles.dataLabel}>CLEARANCE_LVL</span>
                      <div style={styles.dataValue}>
                        <BarChart size={14} color={colors.primary} style={{ opacity: 0.5 }} />
                        {u.clearanceLevel || u.memberLevel || u.level || 1}
                        <button onClick={() => { setLevelModal(u); setNewLevel(u.clearanceLevel || u.memberLevel || u.level || 1); }} style={styles.inlineEditBtn}>
                            <Edit3 size={10} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={styles.statusSection}>
                    {!u.verified && (
                      <button onClick={() => approveUser(u._id)} style={styles.approveBtn}>GRANT CLEARANCE</button>
                    )}
                    
                    <button 
                      onClick={() => toggleIncomeTaxPayment(u)} 
                      style={{
                        ...styles.taxActionBtn,
                        backgroundColor: u.incomeTaxPaid ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
                        borderColor: u.incomeTaxPaid ? colors.success : colors.warning,
                        color: u.incomeTaxPaid ? colors.success : colors.warning
                      }}
                    >
                      <CheckCircle2 size={11} />
                      {u.incomeTaxPaid ? "TAX: PAID" : "TAX: PENDING"}
                    </button>

                    <div style={u.restricted ? styles.badgeBlocked : styles.badgeActive}>
                      <div style={u.restricted ? styles.pulseRed : styles.pulseGreen} />
                      {u.restricted ? "RESTRICTED" : "OPERATIONAL"}
                    </div>
                  </div>

                  <div style={styles.actionSection}>
                    <div style={styles.dropdownContainer}>
                      <MoreVertical size={18} color={colors.textMuted} />
                      <select 
                        style={styles.dropdown}
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "restrict") toggleRestriction(u);
                          if (val === "delete") setDeleteConfirm(u);
                          if (val === "credentials") { setCredModal(u); setNewPassword(""); setNewPin(""); }
                        }}
                      >
                        <option value="" disabled>EXECUTE</option>
                        <option value="credentials">OVERRIDE SECURITY</option>
                        <option value="restrict">{u.restricted ? "RESTORE ACCESS" : "REVOKE ACCESS"}</option>
                        <option value="delete">PURGE DATA</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* OVERLAY MODALS */}
      {(walletModal || levelModal || credModal || deleteConfirm) && <div style={styles.overlay} />}

      {walletModal && (
        <div style={styles.modalWrapper}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <Wallet color={colors.primary} size={20} />
              <h3 style={styles.modalTitle}>ALLOCATION ADJUSTMENT</h3>
            </div>
            <p style={styles.modalSub}>NODE: {walletModal.email}</p>
            <select value={walletType} onChange={(e)=>setWalletType(e.target.value)} style={styles.modalInput}>
              <option value="add">CREDIT_INCREMENT (+)</option>
              <option value="deduct">CREDIT_DECREMENT (-)</option>
            </select>
            <input type="number" placeholder="ENTER AMOUNT" value={walletAmount} onChange={(e)=>setWalletAmount(e.target.value)} style={styles.modalInput}/>
            <div style={styles.modalActions}>
              <button onClick={submitWalletAdjust} style={styles.saveBtn}>COMMIT CHANGES</button>
              <button onClick={()=>setWalletModal(null)} style={styles.cancelBtn}>ABORT</button>
            </div>
          </div>
        </div>
      )}

      {levelModal && (
        <div style={styles.modalWrapper}>
          <div style={styles.modalCard}>
             <div style={styles.modalHeader}>
              <BarChart color={colors.primary} size={20} />
              <h3 style={styles.modalTitle}>PRIORITY OVERRIDE</h3>
            </div>
            <p style={styles.modalSub}>Adjusting level for {levelModal.email}</p>
            <input type="number" value={newLevel} onChange={(e)=>setNewLevel(e.target.value)} style={styles.modalInput}/>
            <div style={styles.modalActions}>
              <button onClick={submitLevelUpdate} style={styles.saveBtn}>SYNC PRIORITY</button>
              <button onClick={()=>setLevelModal(null)} style={styles.cancelBtn}>ABORT</button>
            </div>
          </div>
        </div>
      )}

      {credModal && (
        <div style={styles.modalWrapper}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <Lock color={colors.primary} size={20} />
              <h3 style={styles.modalTitle}>SECURITY OVERRIDE</h3>
            </div>
            <p style={styles.modalSub}>NODE: {credModal.email}</p>
            <label style={styles.modalLabel}><Key size={12} /> NEW ACCESS KEY</label>
            <input type="text" placeholder="NO CHANGE" value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} style={styles.modalInput}/>
            <label style={styles.modalLabel}><ShieldAlert size={12} /> NEW SECURITY PIN</label>
            <input type="text" placeholder="NO CHANGE" value={newPin} onChange={(e)=>setNewPin(e.target.value)} style={styles.modalInput}/>
            <div style={styles.modalActions}>
              <button onClick={submitCredentialUpdate} style={styles.saveBtn}>UPDATE SECURITY</button>
              <button onClick={()=>setCredModal(null)} style={styles.cancelBtn}>ABORT</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={styles.modalWrapper}>
          <div style={{...styles.modalCard, borderColor: colors.accent}}>
            <div style={styles.modalHeader}>
              <XCircle color={colors.accent} size={20} />
              <h3 style={{...styles.modalTitle, color: colors.accent}}>PURGE PROTOCOL</h3>
            </div>
            <p style={styles.modalSub}>Permanently delete node record: <b>{deleteConfirm.email}</b>?</p>
            <div style={styles.modalActions}>
              <button onClick={executeDelete} style={{...styles.saveBtn, backgroundColor: colors.accent}}>CONFIRM PURGE</button>
              <button onClick={()=>setDeleteConfirm(null)} style={styles.cancelBtn}>ABORT</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { display: "flex", backgroundColor: colors.deepBg, minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  mainContent: { flexGrow: 1, transition: "padding-left 0.5s cubic-bezier(0.16, 1, 0.3, 1)", width: "100%" },
  toggleBtn: { background: "rgba(3, 248, 215, 0.1)", border: `1px solid ${colors.border}`, cursor: "pointer", marginRight: "10px", padding: "8px", borderRadius: "8px", display: "flex", alignItems: "center" },
  topBar: { 
    background: colors.surface, 
    backdropFilter: "blur(10px)",
    padding: "20px 40px", 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    borderBottom: `1px solid ${colors.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 10
  },
  headerText: { display: "flex", flexDirection: "column" },
  title: { margin: 0, fontSize: "18px", fontWeight: "900", letterSpacing: "3px", color: "#fff" },
  subTitle: { margin: "4px 0 0 0", fontSize: "9px", color: colors.primary, fontWeight: "800", letterSpacing: "1px" },
  topBarStats: { background: "rgba(0,0,0,0.3)", padding: "10px 20px", borderRadius: "12px", border: `1px solid ${colors.border}` },
  statLabel: { fontSize: "9px", fontWeight: "900", color: colors.textMuted, marginRight: "10px" },
  statValue: { fontSize: "18px", fontWeight: "900", color: colors.primary, fontFamily: "monospace" },
  toast: { 
    position: "fixed", top: "100px", right: "40px", padding: "12px 20px", 
    background: colors.surface, border: "1px solid", borderRadius: "8px", 
    fontWeight: "800", fontSize: "10px", zIndex: 2000, display: "flex", alignItems: "center", gap: "10px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.5)"
  },
  pagePadding: { padding: "40px" },
  searchWrapper: { maxWidth: "800px", margin: "0 auto 40px auto", position: "relative" },
  searchIcon: { position: "absolute", left: "20px", top: "50%", transform: "translateY(-50%)", color: colors.primary },
  searchField: { 
    width: "100%", padding: "18px 20px 18px 55px", borderRadius: "15px", 
    background: "rgba(255,255,255,0.03)", border: `1px solid ${colors.border}`, 
    outline: "none", fontSize: "11px", fontWeight: "700", color: "#fff", letterSpacing: "1px" 
  },
  listContainer: { display: "flex", flexDirection: "column", gap: "12px" },
  userBar: { 
    background: colors.surface, padding: "20px", borderRadius: "15px", 
    display: "flex", alignItems: "center", justifyContent: "space-between", 
    border: `1px solid ${colors.border}`, transition: "0.3s"
  },
  userMainInfo: { display: "flex", alignItems: "center", gap: "20px", flex: 1.5 },
  avatar: { 
    width: "45px", height: "45px", background: "rgba(3, 248, 215, 0.05)", 
    borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", 
    border: `1px solid ${colors.border}` 
  },
  nameSection: { display: "flex", flexDirection: "column" },
  userName: { fontSize: "13px", fontWeight: "900", color: "#fff", letterSpacing: "0.5px" },
  userEmail: { fontSize: "10px", color: colors.textMuted, fontWeight: "600", marginTop: "2px" },
  idWrapper: { flex: 1, textAlign: "center" },
  idLabel: { display: "block", fontSize: "8px", color: colors.textMuted, fontWeight: "900", marginBottom: "4px" },
  highlightedId: { color: colors.primary, fontWeight: "900", fontSize: "10px", background: "rgba(0,0,0,0.4)", padding: "4px 10px", borderRadius: "6px", border: `1px solid ${colors.border}` },
  financeSection: { display: "flex", gap: "40px", flex: 2, justifyContent: "center" },
  dataBlock: { textAlign: "center" },
  dataLabel: { display: "block", fontSize: "8px", color: colors.textMuted, fontWeight: "900", letterSpacing: "1px" },
  dataValue: { fontWeight: "900", color: "#fff", fontSize: "15px", display: "flex", alignItems: "center", gap: "8px", marginTop: "6px", fontFamily: "monospace" },
  inlineEditBtn: { background: "transparent", border: `1px solid ${colors.border}`, borderRadius: "6px", color: colors.primary, padding: "4px", cursor: "pointer" },
  statusSection: { display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", flex: 1.5 },
  statusMsg: { textAlign: "center", padding: "60px", color: colors.textMuted, fontWeight: "800", fontSize: "11px", letterSpacing: "2px" },
  taxActionBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    width: "115px",
    padding: "5px 10px",
    borderRadius: "8px",
    fontSize: "9px",
    fontWeight: "900",
    cursor: "pointer",
    border: "1px solid",
    transition: "all 0.2s ease"
  },
  badgeActive: { 
    background: "rgba(16, 185, 129, 0.1)", color: colors.success, padding: "6px 12px", 
    borderRadius: "8px", fontSize: "9px", fontWeight: "900", display: "flex", alignItems: "center", gap: "8px",
    width: "115px", justifyContent: "center"
  },
  badgeBlocked: { 
    background: "rgba(255, 45, 117, 0.1)", color: colors.accent, padding: "6px 12px", 
    borderRadius: "8px", fontSize: "9px", fontWeight: "900", display: "flex", alignItems: "center", gap: "8px",
    width: "115px", justifyContent: "center"
  },
  pulseGreen: { width: "6px", height: "6px", background: colors.success, borderRadius: "50%", boxShadow: `0 0 10px ${colors.success}` },
  pulseRed: { width: "6px", height: "6px", background: colors.accent, borderRadius: "50%", boxShadow: `0 0 10px ${colors.accent}` },
  approveBtn: { background: colors.primary, color: "#000", border: "none", padding: "6px 12px", borderRadius: "8px", fontSize: "9px", fontWeight: "900", cursor: "pointer", width: "115px" },
  actionSection: { flex: 0.5, display: "flex", justifyContent: "flex-end" },
  dropdownContainer: { position: "relative", display: "flex", alignItems: "center" },
  dropdown: { 
    position: "absolute", opacity: 0, width: "100%", height: "100%", cursor: "pointer"
  },
  overlay: { position: "fixed", top:0, left:0, width: "100%", height: "100%", background: "rgba(0,0,0,0.8)", backdropFilter: "blur(5px)", zIndex: 1000 },
  modalWrapper: { position: "fixed", top:0, left:0, width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1001 },
  modalCard: { background: colors.surface, padding: "40px", borderRadius: "25px", width: "90%", maxWidth: "450px", border: `1px solid ${colors.border}`, boxShadow: "0 20px 50px rgba(0,0,0,0.5)" },
  modalHeader: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "15px" },
  modalTitle: { margin: 0, color: "#fff", fontSize: "16px", fontWeight: "900", letterSpacing: "2px" },
  modalSub: { margin: "0 0 30px 0", fontSize: "10px", color: colors.textMuted, fontWeight: "700", borderBottom: `1px solid ${colors.border}`, paddingBottom: "10px" },
  modalLabel: { fontSize: "9px", fontWeight: "900", color: colors.primary, letterSpacing: "1.5px", display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" },
  modalInput: { 
    width: "100%", padding: "15px", marginBottom: "20px", borderRadius: "12px", 
    background: "#000", border: `1px solid ${colors.border}`, color: colors.primary, 
    fontSize: "13px", fontWeight: "700", outline: "none" 
  },
  modalActions: { display: "flex", gap: "15px", marginTop: "10px" },
  saveBtn: { flex: 1, padding: "16px", background: colors.primary, color: "#000", border: "none", borderRadius: "12px", fontWeight: "900", cursor: "pointer", fontSize: "11px" },
  cancelBtn: { flex: 1, padding: "16px", background: "transparent", color: "#fff", border: `1px solid ${colors.border}`, borderRadius: "12px", fontWeight: "900", cursor: "pointer", fontSize: "11px" },
};

export default ManageUsers;